/**
 * SipTrunksPage — tenant-admin SIP-trunk list.
 *
 * Uses `AdminListScaffold` for the same list-page chrome as every other
 * telephony-management tab. The SIP-trunk-specific `reloadNoticeText` prop
 * surfaces the "telephony stack reload required" notice after each save or
 * delete. Create / edit go through `SipTrunkDrawer`.
 *
 * Backend contract (apps/api/src/routes/admin/sip-trunks/):
 *   GET    /admin/sip-trunks       -> { trunks: SipTrunkResponse[] }
 *   POST   /admin/sip-trunks       -> 201 + row (drawer owns)
 *   PATCH  /admin/sip-trunks/:id   -> 200 + row (drawer owns)
 *   DELETE /admin/sip-trunks/:id   -> 204
 *
 * Permission: `tenant:settings_manage` (TENANT_SETTINGS_MANAGE) — same gate
 * as every other tenant-settings admin page.
 */
import type { JSX } from 'react'

import { AdminListScaffold } from '../../components/admin/AdminListScaffold.js'
import { StatusPill } from '../../components/admin/AdminPageKit.js'
import SummaryCards from '../../components/admin/SummaryCards.js'
import type { ColumnDef } from '../../components/admin/ResourceTable.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import {
  SipTrunkDrawer,
  type SipTrunk,
} from '../../components/admin/sip-trunks/SipTrunkDrawer.js'
import { formatDateTime } from '../../utils/formatDate.js'

/** Per-transport chip colours — TLS reads as "secure", plain transports neutral. */
const TRANSPORT_TONE_DEFAULT = 'bg-slate-50 text-slate-600 ring-slate-200'
const TRANSPORT_TONE: Record<string, string> = {
  tls: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  tcp: 'bg-sky-50 text-sky-700 ring-sky-200',
  udp: TRANSPORT_TONE_DEFAULT,
}

/** Name + service provider, plus a badge on the default-transfer trunk. */
function trunkCell(t: SipTrunk): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="truncate font-semibold text-gray-900">{t.name}</span>
        {t.defaultForExternalTransfers && (
          <span
            className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200"
            title="Default trunk for external transfers"
          >
            Default transfer
          </span>
        )}
      </div>
      <div className="truncate text-xs text-gray-500">
        {t.serviceProviderName ?? 'No service provider set'}
      </div>
    </div>
  )
}

/** Host:port on top, transport chip underneath. */
function endpointCell(t: SipTrunk): JSX.Element {
  const tone = TRANSPORT_TONE[t.transport] ?? TRANSPORT_TONE_DEFAULT
  return (
    <div className="min-w-0">
      <div className="truncate font-mono text-[13px] text-gray-800">
        {t.host}:{t.port}
      </div>
      <span
        className={`mt-1 inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ${tone}`}
      >
        {t.transport}
      </span>
    </div>
  )
}

/** Auth column: username + lock icon when a secret is on file; "(none)" otherwise. */
function authCell(t: SipTrunk): JSX.Element {
  return (
    <div className="min-w-0">
      <span className="inline-flex items-center gap-2 truncate">
        <span className="truncate text-gray-800">{t.username ?? '(none)'}</span>
        {t.hasSecret && (
          <i
            className="pi pi-lock text-xs text-gray-400"
            title="Secret stored"
            aria-label="Secret stored"
          />
        )}
      </span>
      <div className="text-xs text-gray-500">
        {t.sipRegistration ? 'Registers with carrier' : 'IP authenticated'}
      </div>
    </div>
  )
}

/** Concurrent-call cap; 0 is stored as "unlimited". */
function capacityCell(t: SipTrunk): JSX.Element {
  return (
    <span className="text-gray-700">
      {t.maxCalls > 0 ? `${t.maxCalls} calls` : 'Unlimited'}
    </span>
  )
}

const columns: ColumnDef<SipTrunk>[] = [
  { label: 'Trunk', accessor: (t) => trunkCell(t), sortField: 'name' },
  { label: 'SIP Server', accessor: (t) => endpointCell(t), sortField: 'host' },
  { label: 'Authentication', accessor: (t) => authCell(t) },
  { label: 'Max Calls', accessor: (t) => capacityCell(t), sortField: 'maxCalls' },
  {
    label: 'Created Date',
    accessor: (t) => <span className="text-gray-600">{formatDateTime(t.createdAt)}</span>,
    sortField: 'createdAt',
  },
  { label: 'Status', accessor: (t) => <StatusPill active={t.enabled} />, sortField: 'enabled' },
]

function SipTrunksPageInner(): JSX.Element {
  return (
    <AdminListScaffold<SipTrunk>
      title="SIP Trunks"
      description="Carrier SIP trunks used for outbound origination."
      endpoint="/admin/sip-trunks"
      responseKey="trunks"
      addLabel="New SIP Trunk"
      tableTitle="All SIP Trunks"
      searchPlaceholder="Search name, provider or host…"
      searchAriaLabel="Search SIP trunks"
      emptyIcon="pi pi-phone"
      emptyDescription="Add a carrier SIP trunk to start placing outbound calls."
      entityLabel="SIP trunk"
      columns={columns}
      totalFrom={() => 0}
      totalLabel="Total trunks"
      totalIcon="pi pi-sitemap"
      reloadNoticeText="Telephony stack reload required to apply changes (operator handles)."
      clientSide
      searchFilter={(t, q) =>
        [t.name, t.serviceProviderName ?? '', t.host, t.username ?? ''].some((v) =>
          v.toLowerCase().includes(q),
        )
      }
      clientSideTiles={(allTrunks) => {
        const activeCount = allTrunks.filter((t) => t.enabled).length
        const registeringCount = allTrunks.filter((t) => t.sipRegistration).length
        return (
          <SummaryCards
            cards={[
              { label: 'Total trunks', value: allTrunks.length, accentColor: '#6366f1' },
              { label: 'Active', value: activeCount, accentColor: '#10b981' },
              { label: 'Registering', value: registeringCount, accentColor: '#8b5cf6' },
            ]}
          />
        )
      }}
      deleteMessage={(t) =>
        `Permanently delete "${t.name}" (${t.host}:${t.port})? Active calls on this trunk will fail until reload.`
      }
      renderDrawer={({ open, item, onClose, onSaved }) => (
        <SipTrunkDrawer
          open={open}
          trunk={item}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    />
  )
}

export default function SipTrunksPage(): JSX.Element {
  return (
    <RequirePermission permission="tenant:settings_manage">
      <SipTrunksPageInner />
    </RequirePermission>
  )
}
